'use client'

import { useState } from 'react'
import { Badge } from '@ui/base/badge'
import { Button } from '@ui/base/button'
import {
  Combobox,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPositioner,
} from '@ui/base/combobox'
import { Field, FieldError, FieldLabel } from '@ui/base/field'
import { Input } from '@ui/base/input'
import { Separator } from '@ui/base/separator'

import { cityOptionLabel, useCitySearch } from '../lib/use-city-search'
import { type FormCity } from '../model/types'
import { CityOptionText } from './city-option-text'

type Props = {
  type: 'fbs' | 'dbs'
  cities: FormCity[]
  onChange: (cities: FormCity[]) => void
  error?: string
}

const typeLabel = { fbs: 'FBS', dbs: 'DBS' } as const

export const CityChooser = ({ type, cities, onChange, error }: Props) => {
  const label = typeLabel[type]
  const requestErrorId = `${type}-city-search-request-error`
  const selectionErrorId = `${type}-city-search-selection-error`
  const manualCityErrorId = `${type}-manual-city-error`
  const manualRegionErrorId = `${type}-manual-region-error`
  const search = useCitySearch()
  const [isManualOpen, setIsManualOpen] = useState(false)
  const [manualCity, setManualCity] = useState('')
  const [manualRegion, setManualRegion] = useState('')
  const [manualCityError, setManualCityError] = useState('')
  const [manualRegionError, setManualRegionError] = useState('')
  const searchDescribedBy = [search.requestError ? requestErrorId : null, error ? selectionErrorId : null]
    .filter(Boolean)
    .join(' ')

  const addCity = (city: FormCity) => {
    const duplicate = cities.some(
      (selected) =>
        selected.name.toLocaleLowerCase('ru') === city.name.toLocaleLowerCase('ru') &&
        selected.regionName.toLocaleLowerCase('ru') === city.regionName.toLocaleLowerCase('ru')
    )
    if (!duplicate) {
      onChange([...cities, city])
    }
    search.reset()
    return !duplicate
  }

  const addManualCity = () => {
    const city = manualCity.trim()
    const region = manualRegion.trim()
    const cityError = city ? '' : 'Укажите город'
    const regionError = region ? '' : 'Укажите регион'

    setManualCityError(cityError)
    setManualRegionError(regionError)
    if (cityError || regionError) {
      return
    }
    if (addCity({ name: city, regionName: region })) {
      setManualCity('')
      setManualRegion('')
      setManualCityError('')
      setManualRegionError('')
    }
  }

  const openManualCity = () => {
    setIsManualOpen(true)
    if (!manualCity.trim()) {
      setManualCity(search.normalizedQuery)
    }
  }

  return (
    <div className="mt-4 space-y-4">
      {/* Черта отделяет выбор городов от переключателя выше: так видно, что поле относится к
          выбранному варианту, а не к списку вариантов целиком. */}
      <Separator />
      <div className="flex max-w-3xl flex-col items-stretch gap-2 sm:flex-row sm:items-end">
        <Field className="max-w-xl flex-1" data-invalid={Boolean(search.requestError || error)}>
          <FieldLabel htmlFor={`${type}-city-search`}>Город или регион для {label}</FieldLabel>
          <Combobox
            items={search.options}
            filter={null}
            value={null}
            onValueChange={(option) => {
              if (option) {
                addCity({ id: option.id, name: option.name, regionName: option.region_name })
              }
            }}
            inputValue={search.query}
            onInputValueChange={(value, details) => {
              if (details.reason === 'input-change') {
                search.changeQuery(value)
              }
            }}
            itemToStringLabel={cityOptionLabel}
            open={search.isPopupOpen}
            onOpenChange={search.setIsListOpen}
            openOnInputClick={false}
          >
            <ComboboxInput
              id={`${type}-city-search`}
              aria-invalid={Boolean(search.requestError || error)}
              aria-describedby={searchDescribedBy || undefined}
              placeholder="Например, Москва"
            />
            <ComboboxPositioner>
              <ComboboxPopup>
                <ComboboxEmpty>Город не найден</ComboboxEmpty>
                <ComboboxList>
                  {search.options.map((option) => (
                    <ComboboxItem key={option.id} value={option}>
                      <ComboboxItemIndicator />
                      <CityOptionText option={option} />
                    </ComboboxItem>
                  ))}
                </ComboboxList>
              </ComboboxPopup>
            </ComboboxPositioner>
          </Combobox>
          <FieldError id={requestErrorId}>{search.requestError}</FieldError>
        </Field>

        {!isManualOpen && (
          <Button type="button" variant="outline" onClick={openManualCity}>
            Добавить вручную
          </Button>
        )}
      </div>

      {isManualOpen && (
        <div className="space-y-3">
          <Field data-invalid={Boolean(manualCityError)}>
            <FieldLabel htmlFor={`${type}-manual-city`}>Город вручную для {label}</FieldLabel>
            <Input
              id={`${type}-manual-city`}
              value={manualCity}
              aria-invalid={Boolean(manualCityError)}
              aria-describedby={manualCityError ? manualCityErrorId : undefined}
              onChange={(event) => {
                setManualCity(event.target.value)
                if (event.target.value.trim()) {
                  setManualCityError('')
                }
              }}
            />
            <FieldError id={manualCityErrorId}>{manualCityError}</FieldError>
          </Field>
          <Field data-invalid={Boolean(manualRegionError)}>
            <FieldLabel htmlFor={`${type}-manual-region`}>Регион вручную для {label}</FieldLabel>
            <Input
              id={`${type}-manual-region`}
              value={manualRegion}
              aria-invalid={Boolean(manualRegionError)}
              aria-describedby={manualRegionError ? manualRegionErrorId : undefined}
              onChange={(event) => {
                setManualRegion(event.target.value)
                if (event.target.value.trim()) {
                  setManualRegionError('')
                }
              }}
            />
            <FieldError id={manualRegionErrorId}>{manualRegionError}</FieldError>
          </Field>
          <Button type="button" variant="outline" onClick={addManualCity}>
            Добавить город для {label}
          </Button>
        </div>
      )}

      {cities.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" aria-label={`Выбранные города для ${label}`}>
          {cities.map((city) => (
            <div key={`${city.name}-${city.regionName}`} className="flex items-center gap-1">
              <Badge variant="secondary">
                {city.name}, {city.regionName}
              </Badge>
              <Button
                type="button"
                aria-label={`Убрать ${city.name}, ${city.regionName}`}
                onClick={() => onChange(cities.filter((item) => item !== city))}
                variant="ghost"
                size="icon-sm"
              >
                ×
              </Button>
            </div>
          ))}
        </div>
      )}
      <FieldError id={selectionErrorId}>{error}</FieldError>
    </div>
  )
}
